import React, { useState, useEffect } from 'react';
import { FaInstagram, FaYoutube, FaTiktok, FaSnapchat, FaSearch, FaPhone, FaEnvelope, FaBirthdayCake, FaTimes, FaArrowLeft, FaUserPlus, FaFilter, FaChevronDown } from 'react-icons/fa';
import { FaLocationDot } from "react-icons/fa6";
import { getMarketplaceUsers, sendInvitationsToAffiliates, getAvailableUsersForCampaign, getUserServicePricing } from '../../../influencer/services/affiliateService';
import { campaignService, brandService } from '../../services/companyAuthService';
import { toast } from 'react-toastify';
import currencyData from '../../../data/currency.json';

const influencerTypes = ['All ContentType', 'fashion', 'travel', 'sports', 'entertainment', 'technology', 'vlogger', 'musician', 'photography', 'health and fitness', 'home decor', 'beauty and personal care'];
const followerRanges = [
  { label: 'All Followers', min: 0, max: Infinity },
  { label: '1-500', min: 1, max: 500 },
  { label: '501-1,000', min: 501, max: 1000 },
  { label: '1K-10K', min: 1000, max: 10000 },
  { label: '10K-100K', min: 10000, max: 100000 },
  { label: '100K+', min: 100000, max: Infinity }
];

const FilterChip = ({ label, onRemove }) => (
  <div className="inline-flex items-center bg-gray-100 rounded-full px-3 py-1 mr-2 mb-2">
    <span className="text-sm text-gray-700">{label}</span>
    <button
      onClick={onRemove}
      className="ml-2 text-gray-500 hover:text-gray-700"
    >
      <FaTimes size={12} />
    </button>
  </div>
);

const FilterSection = ({ title, children }) => {
  const [open, setOpen] = useState(true);

  return (
    <div className="border-b">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex justify-between items-center py-3 font-semibold text-gray-800"
      >
        {title}
        <FaChevronDown
          className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && <div className="pb-3 space-y-2">{children}</div>}
    </div>
  );
};

const AffiliateMarketplace = () => {
  const [selectedType, setSelectedType] = useState('All ContentType');
  const [selectedRange, setSelectedRange] = useState('All Followers');
  const [searchQuery, setSearchQuery] = useState('');
  const [allAffiliates, setAllAffiliates] = useState([]); // Store original data
  const [filteredAffiliates, setFilteredAffiliates] = useState([]); // Store filtered data
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [servicePricing, setServicePricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const affiliatesPerPage = 6;
  const [activeFilters, setActiveFilters] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);

  // New invitation process states
  const [inviteModal, setInviteModal] = useState({
    isOpen: false,
    loading: false
  });
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [brands, setBrands] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [availableAffiliates, setAvailableAffiliates] = useState([]);
  const [invitingAffiliate, setInvitingAffiliate] = useState(null);
  const [selectedAffiliatesForInvite, setSelectedAffiliatesForInvite] = useState(new Set());
  const [selectAllForInvite, setSelectAllForInvite] = useState(false);
  const [bulkInviting, setBulkInviting] = useState(false);

  // Fetch brands
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await brandService.getBrands();
        if (response.success) {
          setBrands(response.brands.filter(brand => brand.status === 'active'));
        }
      } catch (err) {
        console.error('Error fetching brands:', err);
      }
    };
    fetchBrands();
  }, []);

  // Fetch campaigns when brand is selected
  useEffect(() => {
    const fetchCampaigns = async () => {
      if (!selectedBrand) {
        setCampaigns([]);
        return;
      }

      try {
        const response = await campaignService.getCampaigns();
        if (response.success) {
          const brandCampaigns = response.campaigns.filter(campaign =>
            campaign.brandId === selectedBrand && campaign.status === 'active'
          );
          setCampaigns(brandCampaigns);
        }
      } catch (err) {
        console.error('Error fetching campaigns:', err);
      }
    };
    fetchCampaigns();
  }, [selectedBrand]);

  // Fetch available affiliates when campaign is selected
  useEffect(() => {
    const fetchAvailableAffiliates = async () => {
      if (!selectedCampaign) {
        setAvailableAffiliates([]);
        setSelectedAffiliatesForInvite(new Set());
        setSelectAllForInvite(false);
        return;
      }

      try {
        const response = await getAvailableUsersForCampaign(selectedCampaign);
        if (response.success) {
          const users = response.users.map(user => ({
            id: user._id,
            name: user.username,
            influencerType: user.contentType[0] || 'Not Specified',
            phone: user.mobile || 'Not Available',
            DOB: user.dob ? new Date(user.dob).toLocaleDateString() : 'Not Available',
            email: user.email || 'Not Available',
            followers: user.instagramFollowers || 0,
            profileImage: user.profileImage?.url || null,
            bio: user.bio || 'No bio available',
            platform: 'Instagram',
            engagement: user.engagementRate || 0,
            location: user.country && user.state && user.city && user.postalCode ? `${user.country}, ${user.state}, ${user.city}, ${user.postalCode}` :
              user.country ? user.country :
                user.state ? user.state :
                  user.city ? user.city :
                    user.postalCode ? user.postalCode : 'Not Available',
            contentType: user.contentType || [],
            instagramId: user.instagramId,
            status: user.status
          }));
          setAvailableAffiliates(users);
          // Reset selection when new campaign is selected
          setSelectedAffiliatesForInvite(new Set());
          setSelectAllForInvite(false);
        }
      } catch (err) {
        console.error('Error fetching available affiliates:', err);
      }
    };
    fetchAvailableAffiliates();
  }, [selectedCampaign]);

  // Fetch users data for main marketplace
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await getMarketplaceUsers();

        if (response.success) {
          const users = response.users.map(user => ({
            id: user._id,
            name: user.username,
            influencerType: user.contentType[0] || 'Not Specified',
            phone: user.mobile || 'Not Available',
            DOB: user.dob ? new Date(user.dob).toLocaleDateString() : 'Not Available',
            email: user.email || 'Not Available',
            followers: user.instagramFollowers || 0,
            profileImage: user.profileImage?.url || null,
            bio: user.bio || 'No bio available',
            platform: 'Instagram',
            engagement: user.engagementRate || 0,
            location: user.country && user.state && user.city && user.postalCode ? `${user.country}, ${user.state}, ${user.city}, ${user.postalCode}` :
              user.country ? user.country :
                user.state ? user.state :
                  user.city ? user.city :
                    user.postalCode ? user.postalCode : 'Not Available',
            contentType: user.contentType || [],
            instagramId: user.instagramId,
            status: user.status
          }));
          setAllAffiliates(users);
          setFilteredAffiliates(users);
        }
      } catch (err) {
        setError(err.message || 'Failed to fetch users');
        console.error('Error fetching users:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Reset filters when search changes
  useEffect(() => {
    setSelectedType('All ContentType');
    setSelectedRange('All Followers');
    setCurrentPage(1);
  }, [searchQuery]);

  const filterAffiliates = () => {
    // Always start with the original data
    let filtered = [...allAffiliates];

    // Filter by influencer type
    if (selectedType !== 'All ContentType') {
      filtered = filtered.filter(affiliate =>
        affiliate.contentType.some(type =>
          type.toLowerCase() === selectedType.toLowerCase()
        )
      );
    }

    // Filter by follower range
    if (selectedRange !== 'All Followers') {
      const range = followerRanges.find(r => r.label === selectedRange);
      if (range) {
        filtered = filtered.filter(affiliate =>
          affiliate.followers >= range.min && affiliate.followers <= range.max
        );
      }
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(affiliate =>
        affiliate.name.toLowerCase().includes(query) ||
        (affiliate.bio && affiliate.bio.toLowerCase().includes(query)) ||
        (affiliate.location && affiliate.location.toLowerCase().includes(query))
      );
    }

    setFilteredAffiliates(filtered);
  };

  // Update filterAffiliates when filters change
  useEffect(() => {
    filterAffiliates();
  }, [selectedType, selectedRange, searchQuery, allAffiliates]);

  // Handle filter changes
  const handleFilterChange = (type, value) => {
    setCurrentPage(1); // Reset to first page when filter changes

    // Update main filter states
    switch (type) {
      case 'contentType':
        setSelectedType(value);
        break;
      case 'followerRange':
        setSelectedRange(value);
        break;
      case 'search':
        setSearchQuery(value);
        break;
      default:
        break;
    }

    // Maintain active filters for UI chips (only for contentType and followerRange)
    if (type === 'contentType' || type === 'followerRange') {
      const newFilters = [...activeFilters];
      const existingFilterIndex = newFilters.findIndex(f => f.type === type);

      const isDefaultValue =
        (type === 'contentType' && value === 'All ContentType') ||
        (type === 'followerRange' && value === 'All Followers');

      if (existingFilterIndex >= 0) {
        if (isDefaultValue) {
          newFilters.splice(existingFilterIndex, 1);
        } else {
          newFilters[existingFilterIndex].value = value;
        }
      } else if (!isDefaultValue) {
        newFilters.push({ type, value });
      }

      setActiveFilters(newFilters);
    }
  };

  const resetFilters = () => {
    setSelectedType('All ContentType');
    setSelectedRange('All Followers');
    setActiveFilters([]);
    setCurrentPage(1);
  };

  const getPlatformIcon = (platform) => {
    switch (platform.toLowerCase()) {
      case 'instagram':
        return <FaInstagram className="text-pink-600" />;
      case 'youtube':
        return <FaYoutube className="text-red-600" />;
      case 'tiktok':
        return <FaTiktok className="text-black" />;
      case 'snapchat':
        return <FaSnapchat className="text-yellow-300" />;
      default:
        return null;
    }
  };

  const formatFollowers = (count) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  const getCurrencySymbol = (currencyCode) => {
    try {
      if (currencyData && currencyData.countries && Array.isArray(currencyData.countries)) {
        const country = currencyData.countries.find(c => c.currency === currencyCode);
        return country ? country.symbol : currencyCode;
      }
      return currencyCode;
    } catch (error) {
      console.error('Error getting currency symbol:', error);
      return currencyCode;
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredAffiliates.length / affiliatesPerPage);
  const startIndex = (currentPage - 1) * affiliatesPerPage;
  const currentAffiliates = filteredAffiliates.slice(startIndex, startIndex + affiliatesPerPage);

  const handleImageClick = (e, affiliate) => {
    e.stopPropagation(); // Prevent card click from triggering
    if (affiliate.profileImage) {
      setSelectedImage(affiliate);
    }
  };

  const handleCardClick = async (affiliate) => {
    setSelectedAffiliate(affiliate);
    // Fetch service pricing data for the selected affiliate
    await fetchServicePricing(affiliate.id);
  };

  const fetchServicePricing = async (userId) => {
    try {
      setPricingLoading(true);
      const response = await getUserServicePricing(userId);
      if (response.success) {
        setServicePricing(response.data);
      } else {
        setServicePricing(null);
      }
    } catch (error) {
      console.error('Error fetching service pricing:', error);
      setServicePricing(null);
    } finally {
      setPricingLoading(false);
    }
  };

  const handleCloseModal = (e) => {
    if (e.target === e.currentTarget) {
      setSelectedAffiliate(null);
      setSelectedImage(null);
      setServicePricing(null);
    }
  };

  const getProfileDisplay = (affiliate) => {
    if (affiliate.profileImage) {
      return (
        <img
          src={affiliate.profileImage}
          alt={affiliate.name}
          className="w-full h-full object-cover rounded-full"
        />
      );
    }
    return (
      <div className="w-full h-full flex items-center justify-center bg-orange-500 text-white text-2xl font-bold rounded-full">
        {affiliate.name.charAt(0).toUpperCase()}
      </div>
    );
  };

  // Handle opening invitation modal
  const handleOpenInviteModal = () => {
    setInviteModal({ isOpen: true, loading: false });
    setSelectedBrand('');
    setSelectedCampaign('');
    setAvailableAffiliates([]);
    setSelectedAffiliatesForInvite(new Set());
    setSelectAllForInvite(false);
  };

  // Handle closing invitation modal
  const handleCloseInviteModal = () => {
    setInviteModal({ isOpen: false, loading: false });
    setSelectedBrand('');
    setSelectedCampaign('');
    setAvailableAffiliates([]);
    setInvitingAffiliate(null);
    setSelectedAffiliatesForInvite(new Set());
    setSelectAllForInvite(false);
    setBulkInviting(false);
  };

  // Handle select all for invitation
  const handleSelectAllForInvite = (e) => {
    const checked = e.target.checked;
    setSelectAllForInvite(checked);
    if (checked) {
      const allIds = availableAffiliates.map(affiliate => affiliate.id);
      setSelectedAffiliatesForInvite(new Set(allIds));
    } else {
      setSelectedAffiliatesForInvite(new Set());
    }
  };

  // Handle individual affiliate selection for invitation
  const handleAffiliateSelectForInvite = (affiliateId) => {
    const newSelected = new Set(selectedAffiliatesForInvite);
    if (newSelected.has(affiliateId)) {
      newSelected.delete(affiliateId);
    } else {
      newSelected.add(affiliateId);
    }
    setSelectedAffiliatesForInvite(newSelected);
    setSelectAllForInvite(newSelected.size === availableAffiliates.length);
  };

  // Handle individual affiliate invitation
  const handleInviteAffiliate = async (affiliateId) => {
    if (!selectedCampaign) {
      toast.error('Please select a campaign first');
      return;
    }

    try {
      setInvitingAffiliate(affiliateId);

      // Calculate expiresAt (7 days from now)
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);
      const isoExpiresAt = expiresAt.toISOString();

      const response = await sendInvitationsToAffiliates([affiliateId], selectedCampaign, isoExpiresAt);

      if (response.success) {
        const invitedUsers = response.result.filter(r => r.status === 'invited');
        const alreadyAssociatedUsers = response.alreadyAssociatedUsers || [];
        const failedUsers = response.result.filter(r => r.status === 'failed');

        if (invitedUsers.length > 0) {
          const affiliate = availableAffiliates.find(a => a.id === affiliateId);
          toast.success(`Invitation sent successfully to ${affiliate?.name}!`);
          // Remove the invited affiliate from the list
          setAvailableAffiliates(prev => prev.filter(affiliate => affiliate.id !== affiliateId));
          // Remove from selection if it was selected
          setSelectedAffiliatesForInvite(prev => {
            const newSet = new Set(prev);
            newSet.delete(affiliateId);
            return newSet;
          });
        } else if (alreadyAssociatedUsers.length > 0) {
          toast.warning('This affiliate is already associated with this campaign');
        } else if (failedUsers.length > 0) {
          toast.error('Failed to send invitation');
        }
      } else {
        toast.error(response.message || 'Failed to send invitation');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to send invitation');
    } finally {
      setInvitingAffiliate(null);
    }
  };

  // Handle bulk invitation
  const handleBulkInvite = async () => {
    if (!selectedCampaign) {
      toast.error('Please select a campaign first');
      return;
    }

    if (selectedAffiliatesForInvite.size === 0) {
      toast.warning('Please select at least one influencer to invite');
      return;
    }

    try {
      setBulkInviting(true);

      // Calculate expiresAt (7 days from now)
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);
      const isoExpiresAt = expiresAt.toISOString();

      const userIds = Array.from(selectedAffiliatesForInvite);
      const response = await sendInvitationsToAffiliates(userIds, selectedCampaign, isoExpiresAt);

      if (response.success) {
        const invitedUsers = response.result.filter(r => r.status === 'invited');
        const alreadyAssociatedUsers = response.alreadyAssociatedUsers || [];
        const failedUsers = response.result.filter(r => r.status === 'failed');

        let message = '';
        if (invitedUsers.length > 0) {
          const invitedNames = invitedUsers.map(user => {
            const affiliate = availableAffiliates.find(a => a.id === user.userId);
            return affiliate?.name || 'Unknown';
          });
          message += `Successfully invited ${invitedUsers.length} influencer(s): ${invitedNames.join(', ')}. `;
        }
        if (alreadyAssociatedUsers.length > 0) {
          message += `${alreadyAssociatedUsers.length} influencer(s) were already associated with this campaign. `;
        }
        if (failedUsers.length > 0) {
          message += `Failed to invite ${failedUsers.length} influencer(s).`;
        }

        if (invitedUsers.length > 0) {
          // Remove successfully invited affiliates from the list
          setAvailableAffiliates(prev => prev.filter(affiliate => !invitedUsers.some(user => user.userId === affiliate.id)));
          setSelectedAffiliatesForInvite(new Set());
          setSelectAllForInvite(false);
        }

        if (alreadyAssociatedUsers.length > 0) {
          toast.warning(message);
        } else {
          toast.success(message);
        }
      } else {
        toast.error(response.message || 'Failed to send invitations');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to send invitations');
    } finally {
      setBulkInviting(false);
    }
  };

  // Handle invite all
  const handleInviteAll = async () => {
    if (!selectedCampaign) {
      toast.error('Please select a campaign first');
      return;
    }

    if (availableAffiliates.length === 0) {
      toast.warning('No influencers available to invite');
      return;
    }

    try {
      setBulkInviting(true);

      // Calculate expiresAt (7 days from now)
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);
      const isoExpiresAt = expiresAt.toISOString();

      const userIds = availableAffiliates.map(affiliate => affiliate.id);
      const response = await sendInvitationsToAffiliates(userIds, selectedCampaign, isoExpiresAt);

      if (response.success) {
        const invitedUsers = response.result.filter(r => r.status === 'invited');
        const alreadyAssociatedUsers = response.alreadyAssociatedUsers || [];
        const failedUsers = response.result.filter(r => r.status === 'failed');

        let message = '';
        if (invitedUsers.length > 0) {
          const invitedNames = invitedUsers.map(user => {
            const affiliate = availableAffiliates.find(a => a.id === user.userId);
            return affiliate?.name || 'Unknown';
          });
          message += `Successfully invited all ${invitedUsers.length} influencer(s): ${invitedNames.join(', ')}. `;
        }
        if (alreadyAssociatedUsers.length > 0) {
          message += `${alreadyAssociatedUsers.length} influencer(s) were already associated with this campaign. `;
        }
        if (failedUsers.length > 0) {
          message += `Failed to invite ${failedUsers.length} influencer(s).`;
        }

        if (invitedUsers.length > 0) {
          // Clear all affiliates since all were invited
          setAvailableAffiliates([]);
          setSelectedAffiliatesForInvite(new Set());
          setSelectAllForInvite(false);
        }

        if (alreadyAssociatedUsers.length > 0) {
          toast.warning(message);
        } else {
          toast.success(message);
        }
      } else {
        toast.error(response.message || 'Failed to send invitations');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to send invitations');
    } finally {
      setBulkInviting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-full flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600">Error: {error}</h2>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full mt-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center p-4 lg:mb-0 mb-4">
          <button
            onClick={() => window.history.back()}
            className="text-gray-600 p-2 rounded-full hover:bg-gray-200 lg:mr-4"
          >
            <FaArrowLeft size={22} />
          </button>
          <h2 className="lg:text-3xl text-xl font-bold text-gray-800">Affiliate Marketplace</h2>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-center">
            {/* Search Bar */}
            <div className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search influencers..."
                  value={searchQuery}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
                <FaSearch className="absolute left-3 top-3 text-gray-400" />
              </div>
            </div>

            {/* Filters + Invite */}
            <div className="flex flex-row items-center gap-2 sm:gap-3 md:gap-4 md:ml-4">

              {/* Filter Button */}
              <button
                onClick={() => setFilterOpen(true)}
                className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-full text-sm font-medium text-gray-700 hover:bg-gray-100 whitespace-nowrap transition-colors"
              >
                <FaFilter />
                Filters

                {activeFilters.length > 0 && (
                  <span className="ml-1 bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {activeFilters.length}
                  </span>
                )}
              </button>

              {/* Invite Button */}
              <button
                onClick={handleOpenInviteModal}
                className="flex items-center justify-center gap-2 w-full md:w-auto px-4 py-2 bg-orange-500 text-white border border-orange-500 rounded-full text-sm font-medium hover:bg-orange-600 hover:border-orange-600 whitespace-nowrap transition-colors"
              >
                <FaUserPlus className="text-white" />
                Proceed to Invite
              </button>

            </div>

          </div>

          {/* Active Filters */}
          {activeFilters.length > 0 && (
            <div className="mt-4 flex flex-wrap">
              {activeFilters.map((filter, index) => (
                <FilterChip
                  key={index}
                  label={`${filter.type === 'contentType' ? 'Content Type' : 'Followers'}: ${filter.value}`}
                  onRemove={() => {
                    if (filter.type === 'contentType') {
                      handleFilterChange('contentType', 'All ContentType');
                    } else if (filter.type === 'followerRange') {
                      handleFilterChange('followerRange', 'All Followers');
                    }
                  }}
                />
              ))}
            </div>
          )}
        </div>


        {/* Results Count */}
        <div className="flex justify-between items-center mb-6">
          <p className="text-gray-600 text-sm sm:text-base">
            Found {filteredAffiliates.length} influencers
          </p>
        </div>

        {/* Affiliate Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {currentAffiliates.map(affiliate => (
            <div
              key={affiliate.id}
              className="bg-white rounded-xl shadow-sm overflow-hidden transform transition-transform duration-300 hover:scale-105 hover:shadow-lg relative"
            >
              <div className="p-6" onClick={() => handleCardClick(affiliate)}>
                <div className="flex items-center gap-4 mb-4">
                  <div
                    className="w-16 h-16 rounded-full overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={(e) => handleImageClick(e, affiliate)}
                  >
                    {getProfileDisplay(affiliate)}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{affiliate.name}</h3>
                    <div className="flex items-center gap-2 text-gray-600">
                      {getPlatformIcon(affiliate.platform)}
                      <span>{affiliate.platform}</span>
                    </div>
                  </div>
                </div>

                <p className="text-gray-600 mb-4">{affiliate.bio}</p>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-sm text-gray-500">Followers</p>
                    <p className="font-semibold text-gray-900">{formatFollowers(affiliate.followers)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Engagement</p>
                    <p className="font-semibold text-gray-900">{affiliate.engagement}%</p>
                  </div>
                </div>
                <div className="flex items-center mt-4">
                  <div className="flex items-center gap-2 text-gray-600">
                    <FaLocationDot size={16} className="text-gray-600" />
                    <span className="text-sm text-gray-500">{affiliate.location}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Image Modal */}
        {selectedImage && (
          <div
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
            onClick={handleCloseModal}
          >
            <div
              className="relative rounded-xl max-w-2xl w-full aspect-[4/3] p-4"
              onClick={e => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute -top-4 -right-4 px-3 py-1 text-sm font-medium bg-gray-100 text-gray-700 rounded-md hover:bg-gray-300 transition-all z-10 shadow"
              >
                Close
              </button>
              <div className="w-full h-full flex items-center justify-center">
                {selectedImage.profileImage ? (
                  <img
                    src={selectedImage.profileImage}
                    alt={selectedImage.name}
                    className="w-full h-full object-cover rounded-lg"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-orange-500 text-white text-6xl font-bold rounded-lg">
                    {selectedImage.name.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {/* Details Modal */}
        {selectedAffiliate && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={handleCloseModal}
          >
            <div
              className="bg-white rounded-xl max-w-2xl w-full max-h-[70vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex justify-between items-start mb-6">
                  <div className="flex items-center gap-4">
                    <div className="lg:w-12 lg:h-12 w-10 h-10 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full overflow-hidden">
                      {getProfileDisplay(selectedAffiliate)}
                    </div>
                    <div>
                      <h2 className="lg:text-2xl text-lg font-bold text-gray-900">{selectedAffiliate.name}</h2>
                      <div className="flex items-center gap-2 text-gray-600">
                        {getPlatformIcon(selectedAffiliate.platform)}
                        <span>{selectedAffiliate.platform}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedAffiliate(null)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <FaTimes size={24} />
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold mb-2">Contact Information</h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <FaPhone className="text-gray-600" />
                        <span>{selectedAffiliate.phone}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <FaEnvelope className="text-gray-600" />
                        <span>{selectedAffiliate.email}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <FaBirthdayCake className="text-gray-600" />
                        <span>{selectedAffiliate.DOB}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <FaLocationDot className="text-gray-600" />
                        <span>{selectedAffiliate.location}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold mb-2">Statistics</h3>
                    <div className="space-y-2">
                      <div>
                        <p className="text-sm text-gray-500">Followers</p>
                        <p className="font-semibold text-gray-900">{formatFollowers(selectedAffiliate.followers)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Engagement Rate</p>
                        <p className="font-semibold text-gray-900">{selectedAffiliate.engagement}%</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Influencer Type</p>
                        <p className="font-semibold text-gray-900">{selectedAffiliate.influencerType}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Service Pricing Section */}
                {pricingLoading ? (
                  <div className="bg-gray-50 p-4 rounded-lg mb-6">
                    <h3 className="text-lg font-semibold mb-2">Service Pricing</h3>
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-orange-500"></div>
                      <span className="ml-2 text-gray-600">Loading pricing...</span>
                    </div>
                  </div>
                ) : servicePricing ? (
                  <div className="bg-gray-50 p-4 rounded-lg mb-6">
                    <h3 className="text-lg font-semibold mb-4">Service Pricing</h3>
                    <div className="space-y-4">
                      {/* YouTube Pricing */}
                      {servicePricing.youtube && servicePricing.youtube.currency && (
                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                          <div className="flex items-center gap-2 mb-3">
                            <FaYoutube className="text-red-600 text-xl" />
                            <h4 className="font-semibold text-gray-800">YouTube</h4>
                            <span className="text-sm text-gray-500">({servicePricing.youtube.currency})</span>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            {Object.entries(servicePricing.youtube.pricing).map(([service, price]) => {
                              if (price && price !== 0 && price !== '') {
                                const serviceLabels = {
                                  shortVideo: 'Short Video',
                                  longVideo: 'Long Video',
                                  liveStream: 'Live Stream',
                                  collaboration: 'Collaboration'
                                };
                                return (
                                  <div key={service} className="flex justify-between items-center py-2 px-3 bg-gray-50 rounded">
                                    <span className="text-sm text-gray-700">{serviceLabels[service]}</span>
                                    <span className="font-semibold text-gray-800">
                                      {getCurrencySymbol(servicePricing.youtube.currency)}{parseFloat(price).toFixed(2)}
                                    </span>
                                  </div>
                                );
                              }
                              return null;
                            })}
                          </div>
                        </div>
                      )}

                      {/* Instagram Pricing */}
                      {servicePricing.instagram && servicePricing.instagram.currency && (
                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                          <div className="flex items-center gap-2 mb-3">
                            <FaInstagram className="text-pink-600 text-xl" />
                            <h4 className="font-semibold text-gray-800">Instagram</h4>
                            <span className="text-sm text-gray-500">({servicePricing.instagram.currency})</span>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            {Object.entries(servicePricing.instagram.pricing).map(([service, price]) => {
                              if (price && price !== 0 && price !== '') {
                                const serviceLabels = {
                                  post: 'Post',
                                  story: 'Story',
                                  reel: 'Reel',
                                  collaboration: 'Collaboration'
                                };
                                return (
                                  <div key={service} className="flex justify-between items-center py-2 px-3 bg-gray-50 rounded">
                                    <span className="text-sm text-gray-700">{serviceLabels[service]}</span>
                                    <span className="font-semibold text-gray-800">
                                      {getCurrencySymbol(servicePricing.instagram.currency)}{parseFloat(price).toFixed(2)}
                                    </span>
                                  </div>
                                );
                              }
                              return null;
                            })}
                          </div>
                        </div>
                      )}

                      {/* No Pricing Available */}
                      {(!servicePricing.youtube || !servicePricing.youtube.currency) &&
                        (!servicePricing.instagram || !servicePricing.instagram.currency) && (
                          <div className="text-center py-4 text-gray-500">
                            <p>No service pricing available</p>
                          </div>
                        )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 p-4 rounded-lg mb-6">
                    <h3 className="text-lg font-semibold mb-2">Service Pricing</h3>
                    <div className="text-center py-4 text-gray-500">
                      <p>No service pricing available</p>
                    </div>
                  </div>
                )}

                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <h3 className="text-lg font-semibold mb-2">Bio</h3>
                  <p className="text-gray-600">{selectedAffiliate.bio}</p>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={() => setSelectedAffiliate(null)}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* No Results Message */}
        {filteredAffiliates.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">No influencers found matching your criteria</p>
          </div>
        )}
        {/* Pagination Controls */}
        {filteredAffiliates.length > affiliatesPerPage && (
          <div className="flex justify-between items-center mt-10">
            {/* Previous Button */}
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              className={`px-4 py-2 rounded text-white transition-colors 
        ${currentPage === 1 ? 'bg-gray-500 cursor-not-allowed' : 'bg-orange-500 hover:bg-orange-600'}`}
            >
              Previous
            </button>

            {/* Page Info */}
            <span className="hidden md:inline font-semibold text-gray-700">
              Page {currentPage} of {totalPages}
            </span>

            {/* Next Button */}
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              className={`px-4 py-2 rounded text-white transition-colors 
        ${currentPage === totalPages ? 'bg-gray-400 cursor-not-allowed' : 'bg-orange-500 hover:bg-orange-600'}`}
            >
              Next
            </button>
          </div>
        )}

        {/* Filter Drawer */}
        {filterOpen && (
          <div className="fixed inset-0 z-40">
            {/* Overlay */}
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setFilterOpen(false)}
            />

            {/* Drawer */}
            <div
              className={`
                absolute right-0 bg-white shadow-xl flex flex-col
                w-full sm:w-[380px]
                top-16 bottom-16
                lg:top-16 lg:bottom-0
              `}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <h3 className="text-lg font-semibold">Filters</h3>

                <div className="flex items-center gap-4">
                  <button
                    onClick={resetFilters}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Clear
                  </button>

                  <button
                    onClick={() => setFilterOpen(false)}
                    className="bg-orange-500 text-white px-4 py-1.5 rounded"
                  >
                    Done
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto px-4">
                {/* Content Type Filter */}
                <FilterSection title="Content Type">
                  {influencerTypes.map(type => (
                    <label
                      key={type}
                      className="flex items-center gap-2 text-sm cursor-pointer"
                    >
                      <input
                        type="radio"
                        checked={selectedType === type}
                        onChange={() => handleFilterChange('contentType', type)}
                      />
                      <span>{type}</span>
                    </label>
                  ))}
                </FilterSection>

                {/* Follower Range Filter */}
                <FilterSection title="Followers">
                  {followerRanges.map(range => (
                    <label
                      key={range.label}
                      className="flex items-center gap-2 text-sm cursor-pointer"
                    >
                      <input
                        type="radio"
                        checked={selectedRange === range.label}
                        onChange={() => handleFilterChange('followerRange', range.label)}
                      />
                      <span>{range.label}</span>
                    </label>
                  ))}
                </FilterSection>
              </div>
            </div>
          </div>
        )}

        {/* New Invitation Process Modal */}
        {inviteModal.isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-6xl w-full max-h-[70vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="lg:text-2xl text-xl font-semibold text-gray-900">Influencer Invitation Process</h3>
                  <button
                    onClick={handleCloseInviteModal}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <FaTimes size={24} />
                  </button>
                </div>

                {/* Brand and Campaign Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  {/* Brand Selection */}
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h4 className="lg:text-lg text-base font-semibold mb-4">Select Brand</h4>
                    <select
                      value={selectedBrand}
                      onChange={(e) => {
                        setSelectedBrand(e.target.value);
                        setSelectedCampaign('');
                        setAvailableAffiliates([]);
                      }}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="">Choose a brand...</option>
                      {brands.map(brand => (
                        <option key={brand._id} value={brand._id}>
                          {brand.brandName}
                        </option>
                      ))}
                    </select>
                    {/* {selectedBrand && (
                      <button
                        onClick={() => {
                          setSelectedCampaign('');
                          setAvailableAffiliates([]);
                        }}
                        className="mt-3 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm"
                      >
                        Proceed to Invite
                      </button>
                    )} */}
                  </div>

                  {/* Campaign Selection */}
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h4 className="lg:text-lg text-base font-semibold mb-4">Select Campaign</h4>
                    <select
                      value={selectedCampaign}
                      onChange={(e) => setSelectedCampaign(e.target.value)}
                      disabled={!selectedBrand}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <option value="">Choose a campaign...</option>
                      {campaigns.map(campaign => (
                        <option key={campaign._id} value={campaign._id}>
                          {campaign.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Available Affiliates */}
                {selectedCampaign && (
                  <div className="mb-6">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4">
                      <h4 className="lg:text-lg text-base font-semibold">Available Influencers ({availableAffiliates.length})</h4>

                      {/* Bulk Action Buttons */}
                      <div className="flex flex-wrap gap-2 mt-2 sm:mt-0">
                        {availableAffiliates.length > 0 && (
                          <>
                            <button
                              onClick={handleInviteAll}
                              disabled={bulkInviting}
                              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${bulkInviting
                                  ? 'bg-gray-400 text-white cursor-not-allowed'
                                  : 'bg-green-500 text-white hover:bg-green-600'
                                }`}
                            >
                              {bulkInviting ? (
                                <span className="flex items-center">
                                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  Inviting All...
                                </span>
                              ) : (
                                'Invite All'
                              )}
                            </button>

                            {selectedAffiliatesForInvite.size > 0 && (
                              <button
                                onClick={handleBulkInvite}
                                disabled={bulkInviting}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${bulkInviting
                                    ? 'bg-gray-400 text-white cursor-not-allowed'
                                    : 'bg-blue-500 text-white hover:bg-blue-600'
                                  }`}
                              >
                                {bulkInviting ? (
                                  <span className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Inviting Selected...
                                  </span>
                                ) : (
                                  `Invite Selected (${selectedAffiliatesForInvite.size})`
                                )}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Select All Checkbox */}
                    {availableAffiliates.length > 0 && (
                      <div className="flex items-center mb-4">
                        <input
                          type="checkbox"
                          checked={selectAllForInvite}
                          onChange={handleSelectAllForInvite}
                          className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                        />
                        <label className="ml-2 text-sm text-gray-600">
                          Select All ({availableAffiliates.length} influencers)
                        </label>
                      </div>
                    )}

                    {availableAffiliates.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
                        {availableAffiliates.map(affiliate => (
                          <div
                            key={affiliate.id}
                            className={`bg-white border rounded-lg p-4 hover:shadow-md transition-all ${selectedAffiliatesForInvite.has(affiliate.id)
                                ? 'border-orange-500 bg-orange-50'
                                : 'border-gray-200'
                              }`}
                          >
                            {/* Selection Checkbox */}
                            <div className="flex items-start justify-between mb-3">
                              <input
                                type="checkbox"
                                checked={selectedAffiliatesForInvite.has(affiliate.id)}
                                onChange={() => handleAffiliateSelectForInvite(affiliate.id)}
                                className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500 mt-1"
                              />
                              <div className="w-12 h-12 rounded-full overflow-hidden ml-2">
                                {getProfileDisplay(affiliate)}
                              </div>
                            </div>

                            <div className="mb-3">
                              <h5 className="font-semibold text-gray-900">{affiliate.name}</h5>
                              <div className="flex items-center gap-2 text-gray-600 text-sm">
                                {getPlatformIcon(affiliate.platform)}
                                <span>{affiliate.platform}</span>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                              <div>
                                <p className="text-gray-500">Followers</p>
                                <p className="font-semibold">{formatFollowers(affiliate.followers)}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Engagement</p>
                                <p className="font-semibold">{affiliate.engagement}%</p>
                              </div>
                            </div>

                            <button
                              onClick={() => handleInviteAffiliate(affiliate.id)}
                              disabled={invitingAffiliate === affiliate.id}
                              className={`w-full px-4 py-2 rounded-lg text-sm font-medium transition-colors ${invitingAffiliate === affiliate.id
                                  ? 'bg-gray-400 text-white cursor-not-allowed'
                                  : 'bg-orange-500 text-white hover:bg-orange-600'
                                }`}
                            >
                              {invitingAffiliate === affiliate.id ? (
                                <span className="flex items-center justify-center">
                                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  Inviting...
                                </span>
                              ) : (
                                'Invite'
                              )}
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <p>No available influencers found for this campaign</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Close Button */}
                <div className="flex justify-end">
                  <button
                    onClick={handleCloseInviteModal}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AffiliateMarketplace; 